import { MathQuestionGenerator, challengeBandForRoll } from "./questions";
import { SeededRng, type RandomSeed } from "./rng";
import {
  MATH_SKILLS,
  clampMathTier,
  type AnswerFeedback,
  type ChallengeBand,
  type ChoiceId,
  type MasteryBySkill,
  type MathAnswer,
  type MathQuestion,
  type MathSkill,
  type MathTier,
  type SkillMasteryState,
} from "./types";

interface MutableSkillMastery {
  currentTier: MathTier;
  recentResults: boolean[];
  consecutiveMisses: number;
  supportActive: boolean;
  totalAttempts: number;
  totalCorrect: number;
}

export interface AdaptiveMathOptions {
  readonly seed: RandomSeed;
  readonly initialTier?: MathTier;
  readonly initialTiers?: Readonly<Partial<Record<MathSkill, MathTier>>>;
  readonly initialMastery?: Readonly<Partial<Record<MathSkill, Partial<SkillMasteryState>>>>;
}

export interface NextQuestionOptions {
  readonly skill?: MathSkill;
  /** Keeps later stages progressively challenging while support mode can still step down. */
  readonly minimumTier?: MathTier;
}

function nonNegativeInteger(value: number | undefined): number {
  return Number.isFinite(value) ? Math.max(0, Math.floor(value ?? 0)) : 0;
}

function createMastery(tier: MathTier, initial?: Partial<SkillMasteryState>): MutableSkillMastery {
  const totalAttempts = nonNegativeInteger(initial?.totalAttempts);
  return {
    currentTier: clampMathTier(initial?.currentTier ?? tier),
    recentResults: initial?.recentResults?.filter((result): result is boolean => typeof result === "boolean").slice(-5) ?? [],
    consecutiveMisses: nonNegativeInteger(initial?.consecutiveMisses),
    supportActive: Boolean(initial?.supportActive),
    totalAttempts,
    totalCorrect: Math.min(totalAttempts, nonNegativeInteger(initial?.totalCorrect)),
  };
}

function sameAnswer(left: MathAnswer, right: MathAnswer): boolean {
  return typeof left === typeof right && left === right;
}

function tierForBand(currentTier: MathTier, band: ChallengeBand): MathTier {
  if (band === "review") return clampMathTier(currentTier - 1);
  if (band === "stretch") return clampMathTier(currentTier + 1);
  return currentTier;
}

export function updateMastery(
  state: SkillMasteryState,
  correct: boolean,
): { readonly state: SkillMasteryState; readonly feedback: Omit<AnswerFeedback, "correctAnswer"> } {
  const previousTier = state.currentTier;
  const wasSupported = state.supportActive;
  const recentResults = [...state.recentResults, correct].slice(-5);
  const consecutiveMisses = correct ? 0 : state.consecutiveMisses + 1;
  let supportActive = correct ? false : state.supportActive || consecutiveMisses >= 2;
  let currentTier = state.currentTier;
  let promoted = false;

  if (
    currentTier < 6 &&
    recentResults.length === 5 &&
    recentResults.filter(Boolean).length >= 4
  ) {
    currentTier = clampMathTier(currentTier + 1);
    promoted = true;
    recentResults.length = 0;
    supportActive = false;
  }

  const nextState: SkillMasteryState = {
    currentTier,
    recentResults,
    consecutiveMisses,
    supportActive,
    totalAttempts: state.totalAttempts + 1,
    totalCorrect: state.totalCorrect + (correct ? 1 : 0),
  };

  return {
    state: nextState,
    feedback: {
      correct,
      promoted,
      supportActivated: !wasSupported && supportActive,
      supportActive,
      previousTier,
      currentTier,
    },
  };
}

/**
 * Owns selection and mastery. Timing is deliberately absent from the API: only
 * correctness affects progress.
 */
export class AdaptiveMathEngine {
  private readonly selectionRng: SeededRng;
  private readonly generator: MathQuestionGenerator;
  private readonly mastery: Record<MathSkill, MutableSkillMastery>;
  private readonly issuedQuestions = new Map<string, MathQuestion>();

  public constructor(options: AdaptiveMathOptions) {
    const defaultTier = options.initialTier ?? 1;
    this.selectionRng = new SeededRng(`${String(options.seed)}:selection`);
    this.generator = new MathQuestionGenerator(`${String(options.seed)}:questions`);
    this.mastery = Object.fromEntries(
      MATH_SKILLS.map((skill) => [
        skill,
        createMastery(
          options.initialTiers?.[skill] ?? defaultTier,
          options.initialMastery?.[skill],
        ),
      ]),
    ) as Record<MathSkill, MutableSkillMastery>;
  }

  public nextQuestion(options: NextQuestionOptions = {}): MathQuestion {
    const supportedSkills = MATH_SKILLS.filter((skill) => this.mastery[skill].supportActive);
    const skill =
      options.skill ??
      (supportedSkills.length > 0
        ? this.selectionRng.pick(supportedSkills)
        : this.selectionRng.pick(MATH_SKILLS));
    const state = this.mastery[skill];
    const supportMode = state.supportActive;
    const challengeBand = supportMode ? "review" : challengeBandForRoll(this.selectionRng.next());
    const adaptiveTier = supportMode
      ? clampMathTier(state.currentTier - 1)
      : tierForBand(state.currentTier, challengeBand);
    const tier = supportMode
      ? adaptiveTier
      : clampMathTier(Math.max(adaptiveTier, options.minimumTier ?? 1));
    const question = this.generator.next({ skill, tier, challengeBand, supportMode });
    this.issuedQuestions.set(question.id, question);
    return question;
  }

  public submitAnswer(
    questionId: string,
    selected: MathAnswer | ChoiceId,
  ): AnswerFeedback {
    const question = this.issuedQuestions.get(questionId);
    if (question === undefined) {
      throw new Error(`Question ${questionId} is unknown or was already answered.`);
    }

    const selectedChoice = question.choices.find(
      (choice) => choice.id === selected || sameAnswer(choice.value, selected as MathAnswer),
    );
    if (selectedChoice === undefined) {
      throw new Error("The submitted answer is not one of this question's choices.");
    }

    this.issuedQuestions.delete(questionId);
    const correct = sameAnswer(selectedChoice.value, question.correctAnswer);
    const updated = updateMastery(this.mastery[question.skill], correct);
    this.mastery[question.skill] = {
      currentTier: updated.state.currentTier,
      recentResults: [...updated.state.recentResults],
      consecutiveMisses: updated.state.consecutiveMisses,
      supportActive: updated.state.supportActive,
      totalAttempts: updated.state.totalAttempts,
      totalCorrect: updated.state.totalCorrect,
    };
    return { ...updated.feedback, correctAnswer: question.correctAnswer };
  }

  public getMastery(): MasteryBySkill {
    const snapshot = {} as Record<MathSkill, SkillMasteryState>;
    for (const skill of MATH_SKILLS) {
      const state = this.mastery[skill];
      snapshot[skill] = { ...state, recentResults: [...state.recentResults] };
    }
    return snapshot;
  }
}
